import React, { useEffect, useState, useCallback } from "react";
import { Search, Users, Building2, CheckCircle, MessageCircle, Inbox, CheckCheck, RefreshCw } from "lucide-react";
import adminService from "../../../../services/adminService";
import "./Adminsuporte.scss";

// ─── FILTROS DE TIPO DE USUÁRIO ──────────────────────────────────────
const USER_FILTERS = [
  { key: "todos", label: "Todos", icon: MessageCircle },
  { key: "empresas", label: "Empresas", icon: Building2 },
  { key: "freelancers", label: "Freelancers", icon: Users },
];

// ─── FILTROS DE STATUS ────────────────────────────────────────────────
const STATUS_FILTERS = [
  { key: "novas", label: "Novas", icon: Inbox },
  { key: "resolvidas", label: "Resolvidas", icon: CheckCheck },
  { key: "todas", label: "Todas", icon: MessageCircle },
];

// ─── STATUS PERSISTIDOS PELO BACKEND ─────────────────────────────────
const STATUS = {
  RESOLVED: "closed",
};

const isResolved = (conversation) =>
  conversation?.status === 3 ||
  String(conversation?.status || "") === "3" ||
  String(conversation?.status || "").toLowerCase() === STATUS.RESOLVED;

export default function AdminSupport() {
  // ─── Estados ──────────────────────────────────────────────────────────
  const [userFilter, setUserFilter] = useState("todos");
  const [statusFilter, setStatusFilter] = useState("novas");
  const [search, setSearch] = useState("");
  const [conversations, setConversations] = useState([]);
  const [filteredConversations, setFilteredConversations] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  
  // ─── Refs ─────────────────────────────────────────────────────────────
  // ─── Contagens ────────────────────────────────────────────────────────
  const getCounts = useCallback(() => {
    const all = conversations;
    const novas = all.filter(c => !isResolved(c));
    const resolvidas = all.filter(isResolved);
    return { 
      novas: novas.length, 
      resolvidas: resolvidas.length, 
      total: all.length 
    };
  }, [conversations]);

  // ─── Agrupar conversas por usuário ──────────────────────────────────
  const groupConversationsByUser = (conversations) => {
    const grouped = {};
    conversations.forEach((conv) => {
      const key = conv.userId || conv.id;
      if (
        !grouped[key] ||
        (conv.lastMessageAt &&
          new Date(conv.lastMessageAt) > new Date(grouped[key].lastMessageAt))
      ) {
        grouped[key] = conv;
      }
    });
    return Object.values(grouped);
  };

  // ─── Filtrar por status ──────────────────────────────────────────────
  const getFilteredByStatus = (convs, status) => {
    if (status === "novas") return convs.filter(c => !isResolved(c));
    if (status === "resolvidas") return convs.filter(isResolved);
    return convs; // "todas"
  };

  const applyStatusFilter = (convs, status) => {
    const filtered = getFilteredByStatus(convs, status);
    setFilteredConversations(filtered);
    return filtered;
  };

  // ─── Carregar conversas (BUSCA NO BACKEND) ──────────────────────────
  const loadConversations = useCallback(async (keepActiveId = false) => {
    setLoading(true);
    try {
      // 
      const data = await adminService.getConversations(userFilter, search);
      const uniqueConversations = groupConversationsByUser(data);
      
      // Atualiza a lista completa
      setConversations(uniqueConversations);
      
      // Aplica filtro de status
      const filtered = applyStatusFilter(uniqueConversations, statusFilter);
      
      // Verifica se a conversa ativa ainda existe
      if (activeId) {
        const stillExists = filtered.some(c => c.id === activeId);
        if (!stillExists) {
          // Se a conversa ativa não existe mais, seleciona a primeira
          setActiveId(filtered.length > 0 ? filtered[0].id : null);
          if (filtered.length === 0) {
            setMessages([]);
          }
        } else {
          // Se ainda existe, recarrega as mensagens para atualizar
          if (activeId) {
            try {
              const messagesData = await adminService.getMessages(activeId);
              setMessages(messagesData);
            } catch (msgError) {
              console.error("Erro ao recarregar mensagens:", msgError);
            }
          }
        }
      } else {
        // Se não tem conversa ativa, seleciona a primeira
        if (filtered.length > 0) {
          setActiveId(filtered[0].id);
        }
      }
    } catch (error) {
      console.error("Erro ao carregar conversas:", error);
      // Mostra erro na UI
      alert("Erro ao carregar conversas. Tente novamente.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [userFilter, search, statusFilter, activeId]);

  // ─── Carregar mensagens da conversa ativa ───────────────────────────
  const loadMessages = useCallback(async (conversationId) => {
    if (!conversationId) {
      setMessages([]);
      return;
    }
    
    try {
      console.log(`📨 Carregando mensagens da conversa: ${conversationId}`);
      const messagesData = await adminService.getMessages(conversationId);
      console.log(`📨 ${messagesData.length} mensagens carregadas`);
      setMessages(messagesData);
    } catch (error) {
      console.error("Erro ao carregar mensagens:", error);
      setMessages([]);
    }
  }, []);

  // ─── Efeito: Carregar conversas na montagem e quando filtros mudam ──
  useEffect(() => {
    loadConversations();
  }, [userFilter, search, statusFilter]);

  // ─── Efeito: Carregar mensagens quando activeId muda ────────────────
  useEffect(() => {
    if (activeId) {
      loadMessages(activeId);
    } else {
      setMessages([]);
    }
  }, [activeId, loadMessages]);

  // ─── Conversa ativa ──────────────────────────────────────────────────
  const activeConversation = filteredConversations.find((c) => c.id === activeId);

  // ─── Enviar mensagem ─────────────────────────────────────────────────
  const handleSend = async () => {
    if (!draft.trim() || !activeId || sending) return;
    
    setSending(true);
    try {
      console.log(`📤 Enviando mensagem para conversa: ${activeId}`);
      const message = await adminService.sendMessage(activeId, draft.trim());
      
      // Adiciona a mensagem localmente
      const newMessage = {
        ...message,
        content: message.message,
        fromAdmin: true,
        senderId: message.senderId || "admin",
      };
      
      setMessages((prev) => [...prev, newMessage]);
      setDraft("");
      
      // Atualiza a prévia na lista de conversas
      const updatedConversations = conversations.map((conv) =>
        conv.id === activeId
          ? {
              ...conv,
              lastMessagePreview: message.message,
              lastMessageAt: new Date().toISOString(),
            }
          : conv
      );
      setConversations(updatedConversations);
      
      // Atualiza a lista filtrada
      const updatedFiltered = filteredConversations.map((conv) =>
        conv.id === activeId
          ? {
              ...conv,
              lastMessagePreview: message.message,
              lastMessageAt: new Date().toISOString(),
            }
          : conv
      );
      setFilteredConversations(updatedFiltered);
      
    } catch (error) {
      console.error("Erro ao enviar mensagem:", error);
      alert("Erro ao enviar mensagem. Tente novamente.");
    } finally {
      setSending(false);
    }
  };

  // ─── Resolver conversa ───────────────────────────────────────────────
  const handleResolve = async () => {
    if (!activeId) return;
    if (!window.confirm("Tem certeza que deseja marcar esta conversa como resolvida?")) return;

    setResolving(true);
    try {
      console.log(`✅ Resolvendo conversa: ${activeId}`);
      await adminService.resolveConversation(activeId);
      
      // 🔄 RECARREGA DO BACKEND para garantir consistência
      await loadConversations(true);
      
      alert("Conversa resolvida com sucesso!");
      
    } catch (error) {
      console.error("Erro ao resolver conversa:", error);
      alert("Erro ao resolver conversa. Tente novamente.");
    } finally {
      setResolving(false);
    }
  };

  // ─── Reabrir conversa ─────────────────────────────────────────────────
  const handleReopen = async () => {
    if (!activeId) return;
    if (!window.confirm("Tem certeza que deseja reabrir esta conversa?")) return;

    setResolving(true);
    try {
      console.log(`🔄 Reabrindo conversa: ${activeId}`);
      await adminService.reopenConversation(activeId);
      
      // 🔄 RECARREGA DO BACKEND para garantir consistência
      await loadConversations(true);
      
      alert("Conversa reaberta com sucesso!");
      
    } catch (error) {
      console.error("Erro ao reabrir conversa:", error);
      alert("Erro ao reabrir conversa. Tente novamente.");
    } finally {
      setResolving(false);
    }
  };

  // ─── Forçar refresh ──────────────────────────────────────────────────
  const handleRefresh = async () => {
    setRefreshing(true);
    await loadConversations();
  };

  // ─── Helpers de UI ───────────────────────────────────────────────────
  const getPlanBadge = (plan) => {
    const planMap = {
      free: "Free",
      gold: "Gold",
      premium: "Premium",
    };
    return planMap[plan?.toLowerCase()] || "Free";
  };

  const getAvatarType = (type) => {
    return type?.toLowerCase() === "company" ? "company" : "freelancer";
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return "";
    const diff = Date.now() - new Date(dateStr).getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    if (hours < 1) return "agora";
    if (hours < 24) return `${hours}h`;
    const days = Math.floor(hours / 24);
    return `${days}d`;
  };

  const counts = getCounts();

  // ─── Render ────────────────────────────────────────────────────────────
  return (
    <div className="admin-support-container">
      {/* ─── TOOLBAR SUPERIOR ─────────────────────────────────────────── */}
      <div className="admin-support-toolbar">
        <div className="admin-support-tabs">
          {USER_FILTERS.map((f) => {
            const Icon = f.icon;
            return (
              <button
                key={f.key}
                className={userFilter === f.key ? "active" : ""}
                onClick={() => setUserFilter(f.key)}
              >
                <Icon size={14} />
                {f.label}
              </button>
            );
          })}
        </div>

        <div className="admin-support-search">
          <Search size={16} className="admin-support-search__icon" />
          <input
            placeholder="Buscar conversa..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button 
            className="admin-support-refresh-btn"
            onClick={handleRefresh}
            disabled={refreshing || loading}
            title="Atualizar conversas"
          >
            <RefreshCw size={16} className={refreshing || loading ? "spinning" : ""} />
          </button>
        </div>
      </div>

      {/* ─── ABAS DE STATUS COM CONTAGENS ────────────────────────────── */}
      <div className="admin-support-status-tabs">
        {STATUS_FILTERS.map((f) => {
          const Icon = f.icon;
          const count = f.key === "novas" ? counts.novas 
                      : f.key === "resolvidas" ? counts.resolvidas 
                      : counts.total;
          return (
            <button
              key={f.key}
              className={`admin-support-status-tab ${statusFilter === f.key ? "active" : ""}`}
              onClick={() => setStatusFilter(f.key)}
            >
              <Icon size={16} />
              {f.label}
              <span className="admin-support-status-tab__count">{count}</span>
            </button>
          );
        })}
      </div>

      {/* ─── MAIN LAYOUT ───────────────────────────────────────────────── */}
      <div className="admin-support">
        {/* LISTA DE CONVERSAS */}
        <div className="admin-support__list">
          {(loading && !refreshing) ? (
            <div className="admin-support__loading">
              <div className="admin-support__spinner" />
              <span>Carregando conversas...</span>
            </div>
          ) : filteredConversations.length === 0 ? (
            <div className="admin-support__empty">
              <MessageCircle size={40} />
              <h3>Nenhuma conversa</h3>
              <p>
                {statusFilter === "novas" 
                  ? "Não há conversas novas para exibir."
                  : statusFilter === "resolvidas"
                  ? "Não há conversas resolvidas."
                  : "Não há conversas para exibir com os filtros atuais."}
              </p>
            </div>
          ) : (
            filteredConversations.map((c) => (
              <div
                key={c.id}
                className={`admin-support__item ${
                  c.id === activeId ? "active" : ""
                } ${isResolved(c) ? "admin-support__item--resolved" : ""}`}
                onClick={() => setActiveId(c.id)}
              >
                <div
                  className={`admin-support__item-avatar admin-support__item-avatar--${getAvatarType(
                    c.userType
                  )}`}
                >
                  {c.userName?.[0]?.toUpperCase() || "U"}
                </div>

                <div className="admin-support__item-info">
                  <div className="admin-support__item-top">
                    <span className="admin-support__item-name">{c.userName}</span>
                    <span className="admin-support__item-time">
                      {formatTime(c.lastMessageAt)}
                    </span>
                  </div>

                  <div className="admin-support__item-bottom">
                    <span className="admin-support__item-type">
                      {c.userType === "Freelancer" ? "Freelancer" : "Empresa"}
                    </span>
                    <span
                      className={`admin-support__item-badge admin-support__item-badge--${c.plan?.toLowerCase() ||
                        "free"}`}
                    >
                      {getPlanBadge(c.plan)}
                    </span>
                    {isResolved(c) && (
                      <span className="admin-support__item-badge admin-support__item-badge--resolved">
                        Resolvido
                      </span>
                    )}
                    {c.unreadCount > 0 && !isResolved(c) && (
                      <span className="admin-support__item-badge admin-support__item-badge--unread">
                        {c.unreadCount}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* THREAD DA CONVERSA */}
        <div className="admin-support__thread">
          {activeConversation ? (
            <>
              {/* Header */}
              <div className="admin-support__thread-header">
                <div className="admin-support__thread-user">
                  <div
                    className={`admin-support__thread-avatar admin-support__thread-avatar--${getAvatarType(
                      activeConversation.userType
                    )}`}
                  >
                    {activeConversation.userName?.[0]?.toUpperCase() || "U"}
                  </div>
                  <div>
                    <div className="admin-support__thread-name">
                      {activeConversation.userName}
                      {isResolved(activeConversation) && (
                        <span className="admin-support__item-badge admin-support__item-badge--resolved">
                          Resolvido
                        </span>
                      )}
                    </div>
                    <div className="admin-support__thread-email">
                      {activeConversation.userEmail}
                    </div>
                  </div>
                </div>

                <div className="admin-support__thread-actions">
                  {isResolved(activeConversation) ? (
                    <button
                      className="admin-support__thread-btn admin-support__thread-btn--reopen"
                      onClick={handleReopen}
                      disabled={resolving}
                    >
                      <CheckCircle size={14} />
                      {resolving ? "Reabrindo..." : "Reabrir"}
                    </button>
                  ) : (
                    <button
                      className="admin-support__thread-btn admin-support__thread-btn--resolve"
                      onClick={handleResolve}
                      disabled={resolving}
                    >
                      {resolving ? "Resolvendo..." : "Resolver"}
                    </button>
                  )}
                </div>
              </div>

              {/* Mensagens */}
              <div className="admin-support__messages">
                {messages.length === 0 ? (
                  <div className="admin-support__empty">
                    <h3>Sem mensagens</h3>
                    <p>Inicie a conversa enviando uma mensagem.</p>
                  </div>
                ) : (
                  messages.map((m) => (
                    <div
                      key={m.id}
                      className={`admin-support__bubble admin-support__bubble--${
                        m.fromAdmin ? "admin" : "user"
                      }`}
                    >
                      {m.content || m.message}
                    </div>
                  ))
                )}
              </div>

              {/* Input */}
              <div className="admin-support__input-row">
                <input
                  className="admin-support__input"
                  placeholder={
                    isResolved(activeConversation)
                      ? "Conversa resolvida"
                      : "Digite sua mensagem..."
                  }
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleSend()}
                  disabled={activeConversation.status === STATUS.RESOLVED || sending}
                />
                <button
                  className="admin-support__send-btn"
                  disabled={sending || isResolved(activeConversation)}
                  onClick={handleSend}
                >
                  {sending ? "Enviando..." : "Enviar"}
                </button>
              </div>
            </>
          ) : (
            <div className="admin-support__empty">
              <MessageCircle size={48} />
              <h3>Selecione uma conversa</h3>
              <p>Escolha uma conversa na lista ao lado para começar.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}